<template>
  <div class="px-4 py-10 sm:px-8">
    <div class="mx-auto max-w-6xl">
      <div class="mb-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <h1 class="text-gray-900 text-32 font-bold">Analytics tracking</h1>

        <!-- Time Period Selector -->
        <base-dropdown
          menu-fixed
          class="nav-item"
          tag="div"
          title-tag="button"
          title-classes="nav-link pr-0"
        >
          <base-button
            data-cy="filter-button"
            class="border-toggle bg-canvas border flex cursor-pointer items-center gap-3 rounded-lg border-gray-100 px-4 py-3 shadow-sm"
            @click.prevent=""
            slot="title-container"
          >
            <span v-if="daysSpan === 7" class="font-main-font">
              <span class="hidden sm:inline">Data for last</span> 7 days
            </span>
            <span v-else-if="daysSpan === 14" class="font-main-font">
              <span class="hidden sm:inline">Data for last</span> 14 days
            </span>
            <span v-else-if="daysSpan === 30" class="font-main-font">
              <span class="hidden sm:inline">Data for last</span> 30 days
            </span>
            <span v-else-if="daysSpan === 60" class="font-main-font">
              <span class="hidden sm:inline">Data for last</span> 60 days
            </span>
            <span v-else-if="daysSpan === 90" class="font-main-font">
              <span class="hidden sm:inline">Data for last</span> 90 days
            </span>
            <span v-else-if="daysSpan === 365" class="font-main-font">
              <span class="hidden sm:inline">Data for last</span> 1 year
            </span>
            <inline-svg
              class="-mr-1 ml-3 inline h-4 w-4 fill-current"
              src="/img/icons/calendar.svg"
            />
          </base-button>
          <template>
            <base-button
              v-for="span in availableDaysSpans"
              :key="span"
              class="new-dropdown-item dark-mode-dropdown-item"
              :class="{
                active: daysSpan === span,
              }"
              @click="updateDaysSpan(span)"
            >
              <inline-svg src="/img/icons/calendar.svg" />
              <span>{{ span === 365 ? '1 year' : span + ' days' }}</span>
            </base-button>
          </template>
        </base-dropdown>
      </div>

      <!-- Add Account Section -->
      <div class="mb-8">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div class="flex-1 space-y-1.5">
            <label class="text-gray-700 text-sm font-medium">Enter username to track</label>
            <div class="relative">
              <span class="text-gray-400 absolute inset-y-0 left-0 flex items-center pl-3">@</span>
              <input
                v-model="newUsername"
                type="text"
                placeholder="username"
                class="border border-gray-200 focus:ring-1 focus:ring-main-color-100 placeholder-gray-400 block w-full rounded-xl py-3 pl-8 text-sm focus:border-main-color-100"
                @keyup.enter="addTrackedUser"
              />
            </div>
          </div>
          <base-button
            class="button small-button primary-button"
            :loading="isAdding"
            @click="addTrackedUser"
          >
            Add
          </base-button>
        </div>
        <p v-if="error" class="mt-3 text-sm text-red-500">{{ error }}</p>
      </div>

      <!-- Tracked Accounts Grid -->
      <div v-if="trackedUsers.length > 0" class="grid grid-cols-1 gap-6">
        <div
          v-for="user in trackedUsers"
          :key="user.uid"
          class="analytics-data-cards border relative flex flex-col rounded-2xl border-gray-90 bg-white"
        >
          <div class="p-6" :class="{ 'pb-0': !user.external }">
            <!-- Card Header -->
            <div class="mb-6 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="h-10 w-10 overflow-hidden rounded-full">
                  <img
                    v-if="user.photoURL"
                    :src="user.photoURL"
                    class="h-full w-full object-cover"
                    :alt="user.username"
                  />
                </div>
                <div>
                  <h3 class="text-md font-semibold leading-tight">@{{ user.username }}</h3>
                  <div
                    v-if="metricsResults[user.uid]"
                    class="text-gray-500 mt-0.5 flex items-center gap-1 text-sm"
                  >
                    <span
                      >{{ formatNumber(metricsResults[user.uid].followersTotal) }} followers</span
                    >
                    <span
                      v-if="!user.external"
                      class="cursor-help font-medium"
                      :title="`Growth over the last ${
                        daysSpan === 365 ? '1 year' : daysSpan + ' days'
                      }`"
                      :class="
                        metricsResults[user.uid].followersGain > 0
                          ? 'text-green-500'
                          : metricsResults[user.uid].followersGain < 0
                          ? 'text-red-500'
                          : 'text-gray-400'
                      "
                    >
                      ({{ metricsResults[user.uid].followersGain > 0 ? '+' : ''
                      }}{{ formatNumber(metricsResults[user.uid].followersGain) }})
                    </span>
                  </div>
                </div>
              </div>
              <button
                class="text-gray-400 hover:bg-red-50 rounded-lg p-2 transition-colors hover:text-red-500"
                title="Stop tracking"
                @click="removeTrackedUser(user.uid)"
              >
                <inline-svg src="/img/icons/close.svg" class="h-4 w-4" />
              </button>
            </div>

            <!-- Card Content / Metrics -->
            <div
              v-if="loadingStates[user.uid]"
              class="flex flex-1 items-center justify-center py-8"
            >
              <div
                class="border-gray-200 h-6 w-6 animate-spin rounded-full border-2 border-t-main-color-100"
              ></div>
            </div>

            <div
              v-else-if="metricsResults[user.uid]"
              class="grid grid-cols-2 gap-y-6 sm:grid-cols-4"
            >
              <!-- Tweets -->
              <div
                class="border-r sm:border-r flex flex-col items-center border-gray-100 last:border-0"
              >
                <span class="mb-1 text-md">Tweets</span>
                <span class="text-gray-900 text-xl font-bold">
                  {{ formatNumber(metricsResults[user.uid].tweetsCount) }}
                </span>
              </div>

              <!-- Impressions -->
              <div class="sm:border-r flex flex-col items-center border-gray-100 last:border-0">
                <span class="mb-1 text-md">Impressions</span>
                <span class="text-gray-900 text-xl font-bold">
                  {{ formatNumber(metricsResults[user.uid].impressions) }}
                </span>
              </div>

              <!-- Engagements/Tweet -->
              <div
                class="border-r sm:border-r flex flex-col items-center border-gray-100 last:border-0"
              >
                <span class="mb-1 text-md">Eng/Tweet</span>
                <span class="text-gray-900 text-xl font-bold">
                  {{ formatNumber(metricsResults[user.uid].engagementsPerTweet, 1) }}
                </span>
              </div>

              <!-- Engagement Rate -->
              <div class="flex flex-col items-center last:border-0">
                <span class="mb-1 text-md">Engagement Rate</span>
                <span class="text-gray-900 text-xl font-bold">
                  {{ formatNumber(metricsResults[user.uid].engagementRate, 2) }}%
                </span>
              </div>
            </div>

            <div v-else class="flex flex-1 items-center justify-center py-8">
              <p class="text-gray-400 text-sm">No data available</p>
            </div>
          </div>

          <!-- View Tweets Toggle -->
          <div v-if="metricsResults[user.uid] && !user.external" class="mt-4">
            <base-button class="button w-full" @click="toggleTable(user.uid)">
              <div class="flex items-center gap-2">
                <span>{{ expandedTables[user.uid] ? 'Hide' : 'View' }} tweets</span>
                <inline-svg
                  src="/img/icons/arrow-down.svg"
                  class="h-3 w-3 transform"
                  :class="{ 'rotate-180': expandedTables[user.uid] }"
                />
              </div>
            </base-button>
          </div>

          <!-- Collapsible Tweets Table -->
          <div v-if="expandedTables[user.uid] && !user.external" class="overflow-hidden">
            <div v-if="loadingTweetsStates[user.uid]" class="flex items-center justify-center py-8">
              <div
                class="border-gray-200 h-6 w-6 animate-spin rounded-full border-2 border-t-main-color-100"
              ></div>
            </div>
            <div v-else-if="tweetsResults[user.uid] && tweetsResults[user.uid].length > 0">
              <div
                class="table-container without-scrollbar"
                style="border-radius: 0px; border-width: 1px 0 0 0"
              >
                <table class="w-full">
                  <thead>
                    <tr>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'time')"
                        :class="getTweetsTableHeaderClass(user.uid, 'time')"
                      >
                        Date
                      </th>
                      <th class="dark-mode-bg bg-white text-left">Tweet</th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'impressions')"
                        :class="getTweetsTableHeaderClass(user.uid, 'impressions')"
                      >
                        Impressions
                      </th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'engagements')"
                        :class="getTweetsTableHeaderClass(user.uid, 'engagements')"
                      >
                        Engagements
                      </th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'engagementRate')"
                        :class="getTweetsTableHeaderClass(user.uid, 'engagementRate')"
                      >
                        Eng. Rate
                      </th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'favoriteCount')"
                        :class="getTweetsTableHeaderClass(user.uid, 'favoriteCount')"
                      >
                        Likes
                      </th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'replyCount')"
                        :class="getTweetsTableHeaderClass(user.uid, 'replyCount')"
                      >
                        Replies
                      </th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'retweetCount')"
                        :class="getTweetsTableHeaderClass(user.uid, 'retweetCount')"
                      >
                        Retweets / Quotes
                      </th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'bookmarkCount')"
                        :class="getTweetsTableHeaderClass(user.uid, 'bookmarkCount')"
                      >
                        Bookmarks
                      </th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'urlLinkClicks')"
                        :class="getTweetsTableHeaderClass(user.uid, 'urlLinkClicks')"
                      >
                        Link Clicks
                      </th>
                      <th
                        class="cursor-pointer text-center"
                        @click="orderTweetsBy(user.uid, 'userProfileClicks')"
                        :class="getTweetsTableHeaderClass(user.uid, 'userProfileClicks')"
                      >
                        Profile Clicks
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    <tr v-for="tweet in tweetsResults[user.uid]" :key="tweet.tweetId">
                      <td class="text-gray-900 whitespace-nowrap px-2 py-4 text-sm font-medium">
                        <div style="min-width: 85px">
                          <a
                            target="_blank"
                            :href="`https://twitter.com/${user.username}/status/${tweet.tweetId}`"
                            class="hover:text-main-color-100"
                          >
                            {{ formatDate(tweet.time) }}
                          </a>
                        </div>
                      </td>
                      <td :title="tweet.status" class="text-gray-500 px-4 py-4 text-sm">
                        <div class="truncate" style="max-width: 250px">
                          {{ tweet.status }}
                        </div>
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{ formatNumber(tweet.impressions) }}
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{ formatNumber(getEngagements(tweet)) }}
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{
                          formatNumber(
                            tweet.impressions > 0
                              ? (getEngagements(tweet) / tweet.impressions) * 100
                              : 0,
                            1,
                          )
                        }}%
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{ formatNumber(tweet.favoriteCount) }}
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{ formatNumber(tweet.replyCount) }}
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{ formatNumber(tweet.retweetCount) }} /
                        {{ formatNumber(tweet.quoteCount) }}
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{ formatNumber(tweet.bookmarkCount) }}
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{ formatNumber(tweet.urlLinkClicks) }}
                      </td>
                      <td class="text-gray-500 px-4 py-4 text-center text-sm">
                        {{ formatNumber(tweet.userProfileClicks) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="flex items-center justify-center py-8">
              <p class="text-gray-400 text-sm italic">No tweets found for this period</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="flex flex-col items-center justify-center py-20 text-center">
        <div class="mb-4 rounded-full bg-gray-50 p-6">
          <inline-svg src="/img/icons/analytics.svg" class="text-gray-300 h-10 w-10" />
        </div>
        <h3 class="text-gray-900 mb-2 text-lg font-bold">No accounts tracked yet</h3>
        <p class="text-gray-500 mx-auto max-w-sm">
          Search for an 𝕏 username above to start tracking their performance metrics.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  const lodash = require('lodash');
  const moment = require('moment-timezone');
  import controller from '@/controller';
  import { mapGetters } from 'vuex';

  export default {
    name: 'AnalyticsTracking',
    data() {
      return {
        newUsername: '',
        isAdding: false,
        error: null,
        daysSpan: 30,
        availableDaysSpans: [7, 14, 30, 60, 90, 365],
        metricsResults: {},
        loadingStates: {},
        tweetsResults: {},
        expandedTables: {},
        loadingTweetsStates: {},
        tweetsOrderParams: {},
        tweetsSortOrders: {},
      };
    },
    computed: {
      ...mapGetters({ userProfile: 'getUserProfile', currentUser: 'getCurrentUser' }),
      trackedUsers() {
        return lodash.get(this.userProfile, 'analyticsTrackingSettings.trackedUsers', []);
      },
    },
    watch: {
      trackedUsers: {
        immediate: true,
        handler(newUsers, oldUsers) {
          // If a new user was added, fetch their metrics
          const addedUsers = lodash.differenceBy(newUsers, oldUsers || [], 'uid');
          addedUsers.forEach((user) => {
            this.fetchMetricsForAccount(user.uid);
          });
        },
      },
      daysSpan() {
        this.fetchAllMetrics();
      },
    },
    mounted() {
      this.fetchAllMetrics();
    },
    methods: {
      async addTrackedUser() {
        if (!this.newUsername) {
          this.error = 'Please enter a username.';
          return;
        }

        const cleanUsername = this.newUsername.replace('@', '').trim();

        if (
          this.trackedUsers.some((u) => u.username.toLowerCase() === cleanUsername.toLowerCase())
        ) {
          this.error = 'This account is already being tracked.';
          return;
        }

        this.isAdding = true;
        try {
          await controller.tracking.addTrackedUser(
            this.currentUser,
            this.userProfile.uid,
            cleanUsername,
          );

          this.newUsername = '';
        } catch (err) {
          console.error('Error adding tracked user:', err);
          this.error = err.response?.data || 'An error occurred while adding the account.';
        } finally {
          this.isAdding = false;
        }
      },

      async removeTrackedUser(uid) {
        try {
          await controller.tracking.removeTrackedUser(this.currentUser, this.userProfile.uid, uid);

          // Clean up local state
          const newMetricsResults = { ...this.metricsResults };
          delete newMetricsResults[uid];
          this.metricsResults = newMetricsResults;
        } catch (err) {
          console.error('Error removing tracked user:', err);
        }
      },

      async fetchAllMetrics() {
        this.trackedUsers.forEach((user) => {
          this.fetchMetricsForAccount(user.uid);
        });
      },

      async fetchMetricsForAccount(uid) {
        this.$set(this.loadingStates, uid, true);
        try {
          const [analytics, followersAnalytics] = await Promise.all([
            controller.analytics.getTweetsAnalytics(uid, this.daysSpan).catch(() => null),
            controller.analytics.getFollowersAnalytics(uid, this.daysSpan).catch(() => null),
          ]);

          const userObj = this.trackedUsers.find((u) => u.uid === uid);
          const isExternal = userObj && userObj.external;

          let followersTotal = 0;
          let followersGain = 0;

          if (isExternal) {
            try {
              const userInfo = await controller.getTwitterUserInfo(
                this.currentUser,
                this.userProfile,
                uid,
              );
              followersTotal = lodash.get(userInfo, 'public_metrics.followers_count', 0);
            } catch (e) {
              console.error(`Error fetching external user info for ${uid}:`, e);
            }
          } else if (
            followersAnalytics &&
            followersAnalytics.totalGrowth &&
            followersAnalytics.totalGrowth.length > 0
          ) {
            const totals = followersAnalytics.totalGrowth;
            const diffs = followersAnalytics.dailyDifference || [];

            followersTotal = totals[totals.length - 1] || 0;

            if (diffs.length > 0) {
              followersGain = lodash.sum(diffs);
            } else {
              followersGain = totals[totals.length - 1] - totals[0];
            }
          }

          if (!analytics || !analytics.totalValues) {
            this.$set(this.metricsResults, uid, {
              tweetsCount: 0,
              impressions: 0,
              engagementsPerTweet: 0,
              engagementRate: 0,
              followersTotal,
              followersGain,
            });
            return;
          }

          const total = analytics.totalValues;
          const engagements = this.calculateEngagements(total);

          this.$set(this.metricsResults, uid, {
            tweetsCount: total.tweetsCount || 0,
            impressions: total.impressions || 0,
            engagementsPerTweet: total.tweetsCount > 0 ? engagements / total.tweetsCount : 0,
            engagementRate: total.impressions > 0 ? (engagements / total.impressions) * 100 : 0,
            followersTotal,
            followersGain,
          });
        } catch (err) {
          console.error(`Error fetching metrics for ${uid}:`, err);
          this.$set(this.metricsResults, uid, null);
        } finally {
          this.$set(this.loadingStates, uid, false);
        }
      },

      calculateEngagements(data) {
        const metrics = [
          data.favoriteCount || 0,
          data.replyCount || 0,
          data.retweetCount || 0,
          data.quoteCount || 0,
          data.bookmarkCount || 0,
          data.urlLinkClicks || 0,
        ];
        return lodash.sum(metrics);
      },

      updateDaysSpan(span) {
        this.daysSpan = span;
      },

      formatNumber(n, decimals = 0) {
        if (n === null || n === undefined) return '0';
        return n.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      },

      async toggleTable(uid) {
        if (this.expandedTables[uid]) {
          this.$set(this.expandedTables, uid, false);
          return;
        }
        this.$set(this.expandedTables, uid, true);

        if (!this.tweetsResults[uid]) {
          await this.fetchTweetsForAccount(uid);
        }

        if (!this.tweetsOrderParams[uid]) {
          this.$set(this.tweetsOrderParams, uid, 'time');
          this.$set(this.tweetsSortOrders, uid, 'desc');
        }
      },

      async fetchTweetsForAccount(uid) {
        this.$set(this.loadingTweetsStates, uid, true);
        try {
          const tweets = await controller.analytics.getTweets(uid, this.daysSpan);
          this.$set(this.tweetsResults, uid, tweets || []);
          // Initial sort
          this.orderTweetsBy(uid, 'time', 'desc');
        } catch (err) {
          console.error(`Error fetching tweets for ${uid}:`, err);
          this.$set(this.tweetsResults, uid, []);
        } finally {
          this.$set(this.loadingTweetsStates, uid, false);
        }
      },

      orderTweetsBy(uid, param, forceOrder = null) {
        const currentParam = this.tweetsOrderParams[uid];
        const currentOrder = this.tweetsSortOrders[uid];

        let newOrder = forceOrder;
        if (!newOrder) {
          newOrder = currentParam !== param ? 'desc' : currentOrder === 'asc' ? 'desc' : 'asc';
        }

        this.$set(this.tweetsOrderParams, uid, param);
        this.$set(this.tweetsSortOrders, uid, newOrder);

        const orderedTweets = lodash.orderBy(
          this.tweetsResults[uid],
          [
            (tweet) => {
              if (param === 'engagements') return this.getEngagements(tweet);
              if (param === 'engagementRate') {
                const eng = this.getEngagements(tweet);
                return tweet.impressions > 0 ? (eng / tweet.impressions) * 100 : 0;
              }
              return tweet[param];
            },
          ],
          [newOrder],
        );
        this.$set(this.tweetsResults, uid, orderedTweets);
      },

      getTweetsTableHeaderClass(uid, param) {
        return this.tweetsOrderParams[uid] === param
          ? this.tweetsSortOrders[uid] === 'desc'
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
          : 'bg-white dark-mode-bg';
      },

      formatDate(date) {
        const timezone =
          this.userProfile && this.userProfile.timezone
            ? this.userProfile.timezone
            : moment.tz.guess();
        return moment.tz(date, timezone).format('MMM D Y');
      },

      getEngagements(tweet) {
        const metrics = [
          tweet.favoriteCount || 0,
          tweet.replyCount || 0,
          tweet.retweetCount || 0,
          tweet.quoteCount || 0,
          tweet.bookmarkCount || 0,
          tweet.userProfileClicks || 0,
          tweet.urlLinkClicks || 0,
        ];
        return lodash.sum(metrics);
      },
    },
  };
</script>

<style scoped></style>
